// PSD image-resource builders.
//
// Section 3 of a PSD file is a list of "image resource blocks" (8BIM
// records).  Each block has a 2-byte ID identifying its purpose.  Photoshop
// emits a fairly consistent baseline set on every save — readers (Photoshop,
// Photopea, libpsd, ImageMagick) all tolerate missing blocks, but a few are
// effectively required for correct round-trip:
//
//   1005 ResolutionInfo   — DPI for both axes + display units
//   1007 DisplayInfo      — RGB display info; PS always writes this
//   1026 LayersGroup      — per-layer grouping flags (1 per layer)
//   1037 GlobalAngle      — document-wide global light angle
//   1039 ICCProfile       — optional ICC profile bytes (we don't embed one)
//   1054 URL              — optional URL associated with the doc
//   1061 CaptionDigest    — MD5 of the layer comp set; optional
//   1065 LayerComps       — layer compositions; optional
//
// This file builds the structures.  The writer copies them verbatim into the
// resource section.  See `parsePsd.ts::parseImageResources` for the reader.
//
// Spec reference: Adobe Photoshop File Formats Specification,
// "Image Resources Section".

import type { PsdResource } from "./types";

/** Build the canonical baseline image-resource set for an RGB/8 document. */
export function buildBaselineImageResources(opts: {
  width: number;
  height: number;
  dpi: number;
  /** Number of layers including section dividers — drives the LayersGroup block. */
  layerCount: number;
  /** Optional explicit global angle (degrees, 0..360).  Default 30 per Photoshop. */
  globalAngle?: number;
  /** Optional ICC profile bytes; emitted as block 1039 if non-empty. */
  iccProfile?: Uint8Array;
  /** Optional URL string; emitted as block 1054 if non-empty. */
  url?: string;
  /** Optional caption digest (16 bytes, MD5).  Defaults to zero hash. */
  captionDigest?: Uint8Array;
  /**
   * Optional guide list; emitted as resource 1032 if non-empty.
   * Each guide has a `kind` ("horizontal" | "vertical") and a `position`
   * in document pixels.
   */
  guides?: ReadonlyArray<{ kind: "horizontal" | "vertical"; position: number }>;
}): PsdResource[] {
  const out: PsdResource[] = [];

  out.push(buildResolutionInfo(opts.dpi));
  out.push(buildDisplayInfo());
  out.push(buildLayersGroup(opts.layerCount));
  out.push(buildGlobalAngle(opts.globalAngle ?? 30));
  out.push(buildPrintFlags());
  out.push(buildVersionInfo());
  out.push(buildCaptionDigest(opts.captionDigest));
  out.push(buildLayerComps());
  if (opts.iccProfile && opts.iccProfile.byteLength > 0) {
    out.push({ id: 1039, name: "", data: new Uint8Array(opts.iccProfile) });
  }
  if (opts.url && opts.url.length > 0) {
    out.push(buildUrl(opts.url));
  }
  if (opts.guides && opts.guides.length > 0) {
    out.push(buildGuideResource(opts.guides));
  }

  return out;
}

/**
 * 1005 — Resolution info.
 *   4 bytes: hRes fixed (16.16) in DPI
 *   2 bytes: hRes unit (1 = px/inch, 2 = px/cm)
 *   2 bytes: width display unit (1 = inches, 2 = cm, 3 = points, 4 = picas, 5 = columns)
 *   4 bytes: vRes fixed (16.16)
 *   2 bytes: vRes unit
 *   2 bytes: height display unit
 * Total: 16 bytes.
 */
export function buildResolutionInfo(dpi: number): PsdResource {
  const buf = new ArrayBuffer(16);
  const view = new DataView(buf);
  const fixedDpi = Math.round(dpi * 65536);
  view.setInt32(0, fixedDpi, false);
  view.setInt16(4, 1, false); // hres unit = px/inch
  view.setInt16(6, 1, false); // width display unit = inches
  view.setInt32(8, fixedDpi, false);
  view.setInt16(12, 1, false); // vres unit = px/inch
  view.setInt16(14, 1, false); // height display unit = inches
  return { id: 1005, name: "", data: new Uint8Array(buf) };
}

/**
 * 1007 — Display info (legacy).  Photoshop always emits this even for files
 * without spot channels.  Modern Photoshop emits 14 bytes per channel; for
 * the baseline RGB doc we emit the minimum (1 spot color slot at zero).
 */
export function buildDisplayInfo(): PsdResource {
  // Minimum 14-byte block: one display-info record.
  //   2 bytes: color space (0 = RGB)
  //   8 bytes: color (4 × u16)
  //   2 bytes: opacity (0..100)
  //   1 byte:  kind (0)
  //   1 byte:  padding
  const buf = new ArrayBuffer(14);
  return { id: 1007, name: "", data: new Uint8Array(buf) };
}

/**
 * 1026 — Layers group information.  One u16 per layer record (including the
 * lsct section dividers Photoshop counts as layers).  Each u16 is the layer
 * group ID; zero means "no group", and Photoshop uses this to remember which
 * layers are clipped together.  We emit all zeros — correct for files that
 * don't use the clipping-group feature.
 */
export function buildLayersGroup(layerCount: number): PsdResource {
  const data = new Uint8Array(Math.max(0, layerCount) * 2);
  return { id: 1026, name: "", data };
}

/**
 * 1037 — Global Angle.  Single i32, degrees * 1.  Layer-style effects with
 * `useGlobalLight: true` resolve their angle from this.  Photoshop's default
 * is 30°.
 */
export function buildGlobalAngle(degrees: number): PsdResource {
  const buf = new ArrayBuffer(4);
  const view = new DataView(buf);
  view.setInt32(0, Math.round(degrees) | 0, false);
  return { id: 1037, name: "", data: new Uint8Array(buf) };
}

/**
 * 1054 — URL.  Variable-length ASCII string, no length prefix (the resource
 * block length conveys it).
 */
export function buildUrl(url: string): PsdResource {
  const bytes = new Uint8Array(url.length);
  for (let i = 0; i < url.length; i++) {
    bytes[i] = url.charCodeAt(i) & 0xff;
  }
  return { id: 1054, name: "", data: bytes };
}

/**
 * 1061 — Caption digest.  16 bytes MD5 of the layer comp data.  We don't
 * actually use layer comps, so emit a deterministic zero hash; readers
 * tolerate this.
 */
export function buildCaptionDigest(explicit?: Uint8Array): PsdResource {
  const data = explicit && explicit.byteLength === 16
    ? new Uint8Array(explicit)
    : new Uint8Array(16);
  return { id: 1061, name: "", data };
}

/**
 * 1065 — Layer comps.  When no comps are configured we emit a stub
 * descriptor body (version 1 + zero items).
 */
export function buildLayerComps(): PsdResource {
  // Minimal stub: 4-byte version=1, then an empty descriptor (16-byte version,
  // empty class ID, zero item count).
  const buf = new ArrayBuffer(4 + 4 + 8 + 4);
  const view = new DataView(buf);
  view.setUint32(0, 1, false);        // version
  view.setUint32(4, 16, false);       // descriptor version
  view.setUint32(8, 0, false);        // class ID name length
  view.setUint32(12, 0, false);       // class ID 4-char (zero)
  view.setUint32(16, 0, false);       // item count
  return { id: 1065, name: "", data: new Uint8Array(buf) };
}

/**
 * 1011 — Print flags.  9 bytes: labels, crop marks, color bars, registration
 * marks, negative, flip, interpolate, caption, print flags.  All zero
 * (Photoshop's defaults).
 */
export function buildPrintFlags(): PsdResource {
  return { id: 1011, name: "", data: new Uint8Array(9) };
}

/**
 * 1057 — Version info.  4-byte version (1) + 1 byte hasRealMergedData (1) +
 * Unicode writer name + Unicode reader name + 4-byte file version (1).
 */
export function buildVersionInfo(): PsdResource {
  const writer = "Pixelpa";
  const reader = "Adobe Photoshop";
  const size = 4 + 1
    + 4 + writer.length * 2  // writer Unicode string
    + 4 + reader.length * 2  // reader Unicode string
    + 4;
  const buf = new ArrayBuffer(size);
  const view = new DataView(buf);
  let pos = 0;
  view.setUint32(pos, 1, false); pos += 4;       // version
  view.setUint8(pos, 1); pos += 1;                // hasRealMergedData
  view.setUint32(pos, writer.length, false); pos += 4;
  for (let i = 0; i < writer.length; i++) {
    view.setUint16(pos, writer.charCodeAt(i), false); pos += 2;
  }
  view.setUint32(pos, reader.length, false); pos += 4;
  for (let i = 0; i < reader.length; i++) {
    view.setUint16(pos, reader.charCodeAt(i), false); pos += 2;
  }
  view.setUint32(pos, 1, false);                  // file version
  return { id: 1057, name: "", data: new Uint8Array(buf) };
}

// -- Resource 1032: guides -------------------------------------------------

/**
 * Build PSD image resource 1032 (grid and guides) from a list of guides.
 *
 * Format (Adobe PSD spec, "Image Resources Section"):
 *   Bytes 0-3:  grid horizontal spacing in 1/32 pixels (uint32, big-endian) — we write 0
 *   Bytes 4-7:  grid vertical spacing in 1/32 pixels (uint32, big-endian) — we write 0
 *   Bytes 8-9:  guide count (uint16, big-endian)
 *   Per guide (5 bytes each):
 *     Bytes 0-3: position in 1/32 pixels (signed int32, big-endian)
 *     Byte  4:   direction — 0 = vertical guide, 1 = horizontal guide
 *
 * This is the inverse of `parseGuideResource` in `parseVmsk.ts`.
 */
export function buildGuideResource(
  guides: ReadonlyArray<{ kind: "horizontal" | "vertical"; position: number }>,
): PsdResource {
  // 4 (hGrid) + 4 (vGrid) + 2 (count) + 5 * n (guides)
  const dataLen = 10 + guides.length * 5;
  const buf = new ArrayBuffer(dataLen);
  const view = new DataView(buf);
  const data = new Uint8Array(buf);

  view.setUint32(0, 0, false); // grid horizontal spacing (unused)
  view.setUint32(4, 0, false); // grid vertical spacing (unused)
  view.setUint16(8, guides.length, false); // guide count

  for (let i = 0; i < guides.length; i++) {
    const g = guides[i]!;
    const off = 10 + i * 5;
    // Position stored in 1/32 px units — multiply by 32 and round.
    view.setInt32(off, Math.round(g.position * 32), false);
    // dir: 0 = vertical guide (at X), 1 = horizontal guide (at Y)
    data[off + 4] = g.kind === "horizontal" ? 1 : 0;
  }

  return { id: 1032, name: "", data };
}

// -- Default blending ranges ----------------------------------------------

/**
 * Default 40-byte "Layer Blending Ranges" sub-block for an RGB/8 layer.
 *
 * Layout per the Adobe spec (5 channels × 8 bytes each = 40 bytes total):
 *   Composite gray (8 bytes): srcBlack(2)/srcWhite(2) for this layer + same for underlying
 *   Channel 0 (8 bytes): same shape
 *   Channel 1
 *   Channel 2
 *   Channel 3 (alpha)
 *
 * The default range is 0..255 (no blending exclusion).  We write the four
 * u8 values packed as expected by Photoshop:
 *   black point 1: (0, 0)         → 0x0000
 *   white point 1: (255, 255)     → 0xFFFF
 *   black point 2: (0, 0)         → 0x0000
 *   white point 2: (255, 255)     → 0xFFFF
 *
 * Photoshop never reads these defaults but it does count on the section
 * being non-empty for layers that have a Layer Style, mask, or blend-if
 * configured — so emitting the 40 bytes is the safer choice.
 */
export function buildDefaultBlendingRanges(): Uint8Array {
  const buf = new ArrayBuffer(40);
  const view = new DataView(buf);
  // Composite gray.
  view.setUint16(0, 0x0000, false);   // this layer black
  view.setUint16(2, 0xffff, false);   // this layer white
  view.setUint16(4, 0x0000, false);   // underlying black
  view.setUint16(6, 0xffff, false);   // underlying white
  // Channels 0..3 (R G B A).  Same pattern.
  for (let c = 0; c < 4; c++) {
    const off = 8 + c * 8;
    view.setUint16(off + 0, 0x0000, false);
    view.setUint16(off + 2, 0xffff, false);
    view.setUint16(off + 4, 0x0000, false);
    view.setUint16(off + 6, 0xffff, false);
  }
  return new Uint8Array(buf);
}
